import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ApiError } from '@/shared/api/client';
import {
  clearSignedOut,
  forgetPasskeyEnrolment,
  isEnrolledHere,
  isPasskeyCancel,
  loginWithPasskey,
  passkeysAvailable,
  rememberAccount,
  rememberedAccount,
  signedOutHere,
} from '@/entities/session/passkey';
import { fetchCaptcha, login, type CaptchaChallenge } from '@/entities/session/api';
import { useAuth } from '@/app/auth';
import { BrandLockup } from '@/shared/ui/Brand';
import { Banner } from '@/shared/ui/Banner';
import { Button } from '@/shared/ui/Button';
import { Content, Screen } from '@/shared/ui/Layout';
import { TextField } from '@/shared/ui/TextField';
import { takeAuthNotice } from './flash';
import { validateEmail } from './validation';
import { useDocumentTitle } from '@/shared/lib/useDocumentTitle';

/**
 * Screen A4 — sign in.
 *
 * Three things here are decisions rather than transcription:
 *
 * - **The lock banner is above the form, not below it** (iOS's order, LoginView.swift:33-46).
 *   A blocked account is not a comment on the password just typed; it is the whole situation,
 *   and it has to be read before the fields rather than after them.
 * - **A 429 disables the button and does not offer a retry.** The rate-limit branch on the
 *   server records ANOTHER failed attempt before it even looks at the password (auth.rs:566-572),
 *   so «tap until it lets me in» actively walks the account toward its 24-hour lock. This is the
 *   one place in the app where the right affordance is no affordance.
 * - **The address is trimmed and validated before it is sent.** Neither mobile client trims on
 *   login, so a trailing space from an autofill breaks a correct password against a server that
 *   compares `LOWER(username)` without trimming — and every rejected attempt is one of ten in
 *   the 24-hour window.
 *
 * **Вход по лицу** appears only when all four of its preconditions hold: the browser does
 * WebAuthn, the device has a platform authenticator, the server has a consumer relying party, and
 * THIS device finished an enrolment for the account it remembers. A button that could not deliver
 * would be worse than its absence, which is why nothing is rendered until the check comes back.
 */
export function LoginScreen() {
  useDocumentTitle('Вход');
  const auth = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ from: '/login' });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  /**
   * Картинка с кодом — сервер просит её после пяти неудач по паре «логин + адрес». Сосед за тем
   * же NAT, пять раз ошибившийся паролем, раньше запирал нашу пару на четверть часа, а десятью
   * попытками — на сутки; теперь он упирается в картинку, а мы входим с первого раза.
   */
  const [captcha, setCaptcha] = useState<CaptchaChallenge | null>(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  /** `null` while the four preconditions are still being checked — see the module comment. */
  const [passkeyReady, setPasskeyReady] = useState<boolean | null>(null);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const account = rememberedAccount();
  /** Face ID открывается сам не больше одного раза за визит на экран — см. эффект ниже. */
  const passkeyAutoTried = useRef(false);

  // A ref, not the `busy` state: two submit events can be dispatched before React re-renders,
  // and each one is a failed attempt on the server's counter.
  const inFlight = useRef(false);

  // Read in an effect rather than in a state initializer — StrictMode invokes initializers
  // twice, and this value can only be taken once.
  useEffect(() => {
    const pending = takeAuthNotice();
    if (pending !== null) setNotice(pending);
  }, []);

  // The email field starts on the account this device last signed in as — the same courtesy the
  // iOS app's `LastEmailStore` provides, and what makes «войти по лицу» about a named account
  // rather than an anonymous one.
  useEffect(() => {
    if (account !== null) setEmail(account);
  }, [account]);

  useEffect(() => {
    if (account === null || !isEnrolledHere()) {
      setPasskeyReady(false);
      return;
    }
    let cancelled = false;
    void passkeysAvailable().then((ok) => {
      if (!cancelled) setPasskeyReady(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [account]);

  // Сессия кончилась (неделя от входа прошла), и человек снова на экране входа — Face ID
  // открывается сам, как в PWA nafta-report: «истекла — прислонился лицом» (владелец, 19.09.2026).
  // Кроме случая, когда в этой вкладке только что нажали «Выйти»: тогда человек, возможно, хочет
  // войти другим аккаунтом, и системное окно поперёк экрана ему мешает.
  useEffect(() => {
    if (passkeyReady !== true || passkeyAutoTried.current || signedOutHere()) return;
    passkeyAutoTried.current = true;
    void submitPasskey(true);
    // Только `passkeyReady`: эффект нужен ровно один раз, когда проверка готова.
  }, [passkeyReady]);

  async function submitPasskey(automatic = false) {
    if (passkeyBusy || busy) return;
    setPasskeyBusy(true);
    setError(null);
    setNotice(null);
    try {
      await loginWithPasskey();
      clearSignedOut();
      await auth.signIn();
      const target: string = search.redirect ?? '/';
      await navigate({ to: target, replace: true });
    } catch (cause) {
      // A cancelled system sheet throws too, and it is not a failure worth a red line — the
      // person closed a dialog they opened. Anything the server refused is worth naming.
      if (isPasskeyCancel(cause)) return;
      if (cause instanceof ApiError) {
        // The enrolment this device believed in is gone — the passkey was deleted, or the
        // account was. Stop offering it rather than leaving a button that always fails.
        forgetPasskeyEnrolment();
        setPasskeyReady(false);
        setError('Вход по лицу больше не настроен на этом устройстве. Войдите с паролем.');
        return;
      }
      // Окно, открытое само, — не просьба человека; молча оставляем кнопку и пароль.
      if (automatic) return;
      setError('Не удалось войти по лицу. Попробуйте пароль.');
    } finally {
      setPasskeyBusy(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (inFlight.current || rateLimited) return;

    if (email.trim().length === 0 || password.length === 0) {
      setError('Введите email и пароль');
      return;
    }
    // Format only, never length: the ceiling of 128 characters applies at registration, and
    // `password-reset/confirm` can set a shorter one (auth.rs:1162-1166). Refusing to send a
    // password the account genuinely has would be a lockout of our own making.
    const emailProblem = validateEmail(email);
    if (emailProblem !== null) {
      setError(emailProblem);
      return;
    }

    inFlight.current = true;
    setBusy(true);
    setError(null);
    setNotice(null);

    try {
      await login(email, password, captcha ? { id: captcha.id, answer: captchaAnswer } : null);
      // Remembered here rather than on the next screen: this is the moment the account on this
      // device is known for certain, and «войти по лицу» needs to know whose passkey it holds.
      rememberAccount(email);
      clearSignedOut();
      await auth.signIn();
      // `replace`, so the back gesture from the home screen does not return to a login form
      // that would immediately bounce forward again.
      const target: string = search.redirect ?? '/';
      await navigate({ to: target, replace: true });
    } catch (cause) {
      applyFailure(cause);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  /** Картинка сгорает при любом ответе, верном или нет, — после каждой попытки нужна новая. */
  async function loadCaptcha(): Promise<void> {
    try {
      setCaptcha(await fetchCaptcha());
      setCaptchaAnswer('');
    } catch {
      setError('Не удалось получить картинку. Попробуйте ещё раз.');
    }
  }

  function applyFailure(cause: unknown) {
    const code = cause instanceof ApiError ? cause.code : undefined;
    const status = cause instanceof ApiError ? cause.status : 0;

    // Branch on `code` first and fall back to the status, the way both mobile clients do:
    // `message` is English (`invalid credentials`, `account locked`) and never shown.
    if (code === 'captcha_required') {
      setLocked(false);
      setRateLimited(false);
      setError(captcha !== null ? 'Код с картинки не подошёл. Введите код с новой картинки.' : null);
      void loadCaptcha();
      return;
    }
    if (code === 'account_locked' || status === 423) {
      setLocked(true);
      setError(null);
      return;
    }
    if (code === 'rate_limited' || status === 429) {
      setLocked(false);
      // Kept out of `error` on purpose: `error` is cleared on the next keystroke, and this
      // message must not be — it is the only thing explaining why the button stays dead.
      // Retrying is worse than waiting: the rate-limit branch records ANOTHER failed attempt
      // (auth.rs:566-572) without even checking the password, so hammering the button walks
      // the account towards the 24-hour lockout.
      setRateLimited(true);
      setError(null);
      return;
    }

    setLocked(false);
    if (code === 'invalid_credentials' || status === 401) {
      // Deliberately indistinguishable between «no such account» and «wrong password»
      // (auth.rs:604-607) — an admin account typed in here also lands on this line.
      setError('Неверный email или пароль.');
      // Решённая картинка сгорела вместе с неверным паролем — к следующей попытке нужна новая.
      if (captcha !== null) void loadCaptcha();
    } else if (code === 'network_error') {
      setError('Нет соединения с сервером. Проверьте интернет и попробуйте снова.');
    } else {
      setError('Не удалось войти. Попробуйте ещё раз.');
    }
  }

  const canSubmit =
    email.trim().length > 0 &&
    password.length > 0 &&
    !rateLimited &&
    (captcha === null || captchaAnswer.trim().length > 0);

  return (
    <Screen>
      <Content width="narrow" centered>
        <BrandLockup size={44} tagline="Надёжная выездная заправка" />

        <h1 className="mt-6 text-center text-[22px] font-extrabold leading-[1.2727]">
          С возвращением
        </h1>
        <p className="mb-6 mt-1.5 text-center text-ink-soft">Войдите, чтобы заказать заправку</p>

        {/* One live region, mounted whether or not it has anything in it: a `role="status"`
            that appears together with its own content is announced unreliably, because there was
            no region to watch until the moment it mattered. */}
        <div role="status">
          {notice !== null && (
            <div className="mb-4">
              <Banner tone="success">{notice}</Banner>
            </div>
          )}

          {locked && (
            <div className="mb-4">
              <Banner
                tone="danger"
                title="Аккаунт временно заблокирован"
                action={
                  <Button
                    variant="secondary"
                    onClick={() => void navigate({ to: '/forgot-password' })}
                  >
                    Восстановить пароль
                  </Button>
                }
              >
                {/* «за последние 24 часа» is Android's wording and it is the real constant
                    (`LOCKOUT_WINDOW_HOURS`, auth.rs:538). The server sends no `Retry-After` and
                    no unlock time, so this sentence is the most precise thing that can honestly
                    be said — a countdown here would be invented. */}
                Слишком много неудачных попыток входа за последние 24 часа. Подождите или
                восстановите пароль.
              </Banner>
            </div>
          )}
        </div>

        <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
          <TextField
            label="Email"
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="you@mail.ru"
            value={email}
            disabled={busy}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(null);
            }}
          />

          <TextField
            label="Пароль"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            value={password}
            disabled={busy}
            onChange={(e) => {
              setPassword(e.target.value);
              setError(null);
            }}
          />

          {captcha !== null && (
            <div className="flex flex-col gap-2" data-testid="login-captcha">
              <img
                src={`data:image/png;base64,${captcha.image_png_base64}`}
                alt="Код с картинки"
                width={220}
                height={90}
                className="h-[90px] w-[220px] rounded-lg bg-white"
              />
              <TextField
                label="Код с картинки"
                inputMode="text"
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                placeholder="5 символов"
                value={captchaAnswer}
                disabled={busy}
                onChange={(e) => {
                  setCaptchaAnswer(e.target.value);
                  setError(null);
                }}
              />
              <button
                type="button"
                className="inline-flex min-h-[44px] items-center self-start px-1 text-[13px] font-semibold text-ink-soft"
                disabled={busy}
                onClick={() => void loadCaptcha()}
              >
                Другая картинка
              </button>
            </div>
          )}

          <div className="-my-2 flex justify-end">
            {/* `ink-soft`, not `accent`: the prototype draws this as a ghost control
                (index.html:571) and iOS follows it. Painted in the accent colour it competes
                with «Войти» for the eye of somebody who has simply mistyped their password.
                The min-height is the 44px touch target — the text alone is 18px tall. */}
            <Link
              to="/forgot-password"
              className="inline-flex min-h-[44px] items-center px-1 text-[13px] font-semibold text-ink-soft"
            >
              Забыли пароль?
            </Link>
          </div>

          {/* Hidden while the lock banner is up: the banner already explains the refusal, and a
              second red line under it would read as a second, different problem. */}
          {rateLimited && (
            <Banner tone="warning" title="Слишком много попыток входа">
              Подождите примерно четверть часа и попробуйте снова. Повторные попытки сейчас
              только приближают блокировку аккаунта на сутки.
            </Banner>
          )}

          {error !== null && !locked && !rateLimited && (
            <p role="alert" className="text-[13px] leading-[1.4] text-accent-soft-ink">
              {error}
            </p>
          )}

          <Button type="submit" isLoading={busy} disabled={!canSubmit}>
            Войти
          </Button>

          {/* Under «Войти», not above it: the password is the path that always works, and this
              one is a shortcut for the device that has already been set up. */}
          {passkeyReady === true && (
            <Button
              variant="secondary"
              isLoading={passkeyBusy}
              disabled={busy}
              onClick={() => void submitPasskey()}
            >
              Войти по лицу
            </Button>
          )}
        </form>

        <p className="mt-6 text-center text-[13px] text-ink-soft">
          Нет аккаунта?{' '}
          {/* Цель не ниже 44px: замер 12.09.2026 дал ссылке 16 в высоту. `inline-flex` растит
              только область нажатия — строка остаётся одной и по центру. */}
          <Link to="/register" className="inline-flex min-h-11 items-center font-bold text-ink">
            Зарегистрироваться
          </Link>
        </p>
      </Content>
    </Screen>
  );
}
